import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'

function BlogThree() {
    const [blogPosts, setBlogPosts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Fetch blog posts data from API
    useEffect(() => {
        const fetchBlogPosts = async () => {
            setLoading(true);
            try {
                const response = await fetch('https://dzsoftech.net/tours/api/airticket/blog/');
                if (!response.ok) {
                    throw new Error('Failed to fetch blog posts');
                }
                const data = await response.json();
                
                if (data.results && data.results.length > 0) {
                    // Get published posts, prioritize featured ones
                    const publishedPosts = data.results
                        .filter(post => post.is_published !== false)
                        .sort((a, b) => {
                            if (a.featured && !b.featured) return -1;
                            if (!a.featured && b.featured) return 1;
                            return new Date(b.published_at) - new Date(a.published_at);
                        })
                        .slice(0, 3); // Get only the first 3 posts for the layout
                    setBlogPosts(publishedPosts);
                } else {
                    // Use fallback data if no posts
                    setBlogPosts(getFallbackPosts());
                }
            } catch (err) {
                console.error('Error fetching blog posts:', err);
                setError(err.message);
                // Use fallback data on error
                setBlogPosts(getFallbackPosts());
            } finally {
                setLoading(false);
            }
        };

        fetchBlogPosts();
    }, []);

    // Fallback blog posts if API fails
    const getFallbackPosts = () => {
        return [
            {
                id: 'fallback-1',
                title: 'Travel agency for those who want to explore the world and try to make adventure',
                excerpt: 'Discover amazing destinations and create unforgettable memories.',
                featured_image_url: '/assets/img/blog/blog_3_1.jpg',
                published_at: '2024-07-05T00:00:00Z',
                slug: 'travel-agency-adventure'
            },
            {
                id: 'fallback-2',
                title: 'The best time to visit japan & enjoy the cherry blossoms',
                excerpt: 'Experience the magical beauty of cherry blossom season in Japan.',
                featured_image_url: '/assets/img/blog/blog_3_2.jpg',
                published_at: '2024-07-07T00:00:00Z',
                slug: 'japan-cherry-blossoms'
            },
            {
                id: 'fallback-3',
                title: 'Hidden history of Japan in the world and try to make adventure',
                excerpt: 'Uncover the secrets and hidden stories of Japanese culture.',
                featured_image_url: '/assets/img/blog/blog_3_3.jpg',
                published_at: '2024-07-10T00:00:00Z',
                slug: 'japan-hidden-history'
            }
        ];
    };

    // Function to get image URL with fallback
    const getImageUrl = (post, fallbackPath) => {
        return post.featured_image_url || fallbackPath || '/assets/img/blog/blog_3_1.jpg';
    };

    // Function to format date
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'long', 
            day: '2-digit' 
        });
    };

    // Function to calculate reading time (rough estimate based on excerpt length)
    const getReadingTime = (text) => {
        const words = text ? text.split(' ').length : 100;
        const minutes = Math.max(1, Math.ceil(words / 200)); // Average reading speed
        return `${minutes} min read`;
    };
    return (
        <section className="bg-smoke overflow-hidden space">
            <div className="container shape-mockup-wrap">
                <div className="row justify-content-lg-between justify-content-center align-items-end">
                    <div className="col-lg">
                        <div className="title-area text-center text-lg-start">
                            <span className="sub-title">Blog and Article</span>
                            <h2 className="sec-title">Blog &amp; Articles From Tourm</h2>
                        </div>
                    </div>
                    <div className="col-lg-auto d-none d-lg-block">
                        <div className="sec-btn">
                            <Link to="/blog" className="th-btn style4 th-icon">
                                See More Articles
                            </Link>
                        </div>
                    </div>
                </div>
                {loading ? (
                    <div className="text-center">
                        <div className="spinner-border" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : (
                    <div className="row gx-24 gy-30">
                        {/* First blog post - larger format */}
                        {blogPosts[0] && (
                            <div className="col-xl-5">
                                <div className="blog-grid th-ani">
                                    <div className="blog-img global-img">
                                        <img 
                                            src={getImageUrl(blogPosts[0], '/assets/img/blog/blog_3_1.jpg')} 
                                            alt={blogPosts[0].title}
                                            onError={(e) => {
                                                e.target.src = '/assets/img/blog/blog_3_1.jpg';
                                            }}
                                        />
                                    </div>
                                    <div className="blog-grid_content">
                                        <div className="blog-meta">
                                            <Link className="author" to="/blog">
                                                {formatDate(blogPosts[0].published_at)}
                                            </Link>
                                            <Link to="/blog">{getReadingTime(blogPosts[0].excerpt)}</Link>
                                        </div>
                                        <h3 className="box-title">
                                            <Link to={`/blog/${blogPosts[0].slug}`}>
                                                {blogPosts[0].title}
                                            </Link>
                                        </h3>
                                        <p className="blog-text d-none d-sm-block">
                                            {blogPosts[0].excerpt}
                                        </p>
                                        <Link to={`/blog/${blogPosts[0].slug}`} className="th-btn style4 th-icon">
                                            Read More
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        )}
                        
                        {/* Second and third blog posts - smaller format */}
                        <div className="col-xl-7">
                            {blogPosts[1] && (
                                <div className="blog-grid style2 th-ani">
                                    <div className="blog-img global-img">
                                        <img 
                                            src={getImageUrl(blogPosts[1], '/assets/img/blog/blog_3_2.jpg')} 
                                            alt={blogPosts[1].title}
                                            onError={(e) => {
                                                e.target.src = '/assets/img/blog/blog_3_2.jpg';
                                            }}
                                        />
                                    </div>
                                    <div className="blog-grid_content">
                                        <div className="blog-meta">
                                            <Link className="author" to="/blog">
                                                {formatDate(blogPosts[1].published_at)}
                                            </Link>
                                            <Link to="/blog">{getReadingTime(blogPosts[1].excerpt)}</Link>
                                        </div>
                                        <h3 className="box-title">
                                            <Link to={`/blog/${blogPosts[1].slug}`}>
                                                {blogPosts[1].title}
                                            </Link>
                                        </h3>
                                        <Link to={`/blog/${blogPosts[1].slug}`} className="th-btn style4 th-icon">
                                            Read More
                                        </Link>
                                    </div>
                                </div>
                            )}
                            {blogPosts[2] && (
                                <div className="blog-grid th-ani style2 mt-24">
                                    <div className="blog-img global-img">
                                        <img 
                                            src={getImageUrl(blogPosts[2], '/assets/img/blog/blog_3_3.jpg')} 
                                            alt={blogPosts[2].title}
                                            onError={(e) => {
                                                e.target.src = '/assets/img/blog/blog_3_3.jpg';
                                            }}
                                        />
                                    </div>
                                    <div className="blog-grid_content">
                                        <div className="blog-meta">
                                            <Link className="author" to="/blog">
                                                {formatDate(blogPosts[2].published_at)}
                                            </Link>
                                            <Link to="/blog">{getReadingTime(blogPosts[2].excerpt)}</Link>
                                        </div>
                                        <h3 className="box-title">
                                            <Link to={`/blog/${blogPosts[2].slug}`}>
                                                {blogPosts[2].title}
                                            </Link>
                                        </h3>
                                        <Link to={`/blog/${blogPosts[2].slug}`} className="th-btn style4 th-icon">
                                            Read More
                                        </Link>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
                <div
                    className="shape-mockup shape1 d-none d-xxl-block"
                    style={{
                        top: "14%",
                        right: "-13%",
                    }}
                >
                    <img src="/assets/img/shape/shape_1.png" alt="shape" />
                </div>
                <div
                    className="shape-mockup shape2 d-none d-xl-block"
                    style={{
                        top: "25%",
                        right: "-12%",
                    }}
                >
                    <img src="/assets/img/shape/shape_2.png" alt="shape" />
                </div>
                <div
                    className="shape-mockup shape3 d-none d-xxl-block"
                    style={{
                        top: "20%",
                        right: "-18%",
                    }}
                >
                    <img src="/assets/img/shape/shape_3.png" alt="shape" />
                </div>
                <div
                    className="shape-mockup movingX d-none d-xxl-block"
                    style={{
                        bottom: "-15%",
                        right: "10%",
                    }}
                >
                    <img src="/assets/img/shape/shape_9.png" alt="shape" />
                </div>
            </div>
        </section>

    )
}

export default BlogThree
